import { Tag, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PageLayout } from '@kolab/dms-base/components/layout'
import { exportDeviceLockUnlock, listDeviceLockUnlock } from '../../api/report.api'
import { reportKeys } from '../../api/queryKeys'
import type { DeviceLockUnlockEvent } from '../../api/types'
import { ReportFilterBar } from '../../components/reports/ReportFilterBar'
import { ExportButton, ReportResults } from '../../components/reports/ReportResults'
import { useReportFilters } from '../../hooks/useReportFilters'
import { useReportPagination } from '../../hooks/useReportPagination'
import { Can, P } from '../../rbac'
import { actionLabel, formatInTz } from '../../utils/formatters'

/**
 * Day-wise Device Lock & Unlock report: successful lock/unlock commands only
 * (executed and acknowledged with SUCCESS), dated by when the device carried
 * them out. Filters live in the URL so dashboard cards can deep-link.
 */
export default function DeviceLockUnlockReport() {
  const filters = useReportFilters({ lockUnlock: true })
  const pagination = useReportPagination<DeviceLockUnlockEvent>({
    queryKey: (page) => reportKeys.lockUnlock.list({ ...filters.query, ...page }),
    queryFn: (page) => listDeviceLockUnlock({ ...filters.query, ...page }),
    pageSize: filters.values.pageSize,
    resetKey: filters.resetKey,
    enabled: !!filters.query,
  })
  const tz = pagination.timezone

  const columns: ColumnsType<DeviceLockUnlockEvent> = [
    { title: 'Date', dataIndex: 'executedDate', key: 'date', width: 120 },
    { title: 'Device ID / Serial', dataIndex: 'deviceId', key: 'deviceId', ellipsis: true },
    { title: 'Device Name', dataIndex: 'deviceName', key: 'deviceName', ellipsis: true, render: dash },
    {
      title: 'Lock/Unlock Date & Time',
      dataIndex: 'executedAt',
      key: 'executedAt',
      width: 190,
      render: (v: string) => formatInTz(v, tz),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (a: string) => <Tag color={a === 'LOCK' ? 'red' : 'green'}>{actionLabel(a)}</Tag>,
    },
    {
      title: 'User / Operator',
      dataIndex: 'operator',
      key: 'operator',
      ellipsis: true,
      render: (op: string, row) =>
        row.operatorLegacy ? (
          <Tooltip title="Sent before the operator was recorded">
            <span>{op || '—'} (legacy)</span>
          </Tooltip>
        ) : (
          dash(op)
        ),
    },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', ellipsis: true, render: dash },
  ]

  return (
    <PageLayout
      filters={
        <ReportFilterBar
          lockUnlock
          values={filters.values}
          error={filters.error}
          timezone={tz}
          hasActiveFilters={filters.hasActiveFilters}
          onChange={filters.setFilters}
          onReset={filters.reset}
        />
      }
      actions={
        <Can permission={P.reports.device_lock_unlock.export}>
          <ExportButton
            disabled={!filters.query || pagination.totalCount === 0}
            onExport={() => exportDeviceLockUnlock(filters.query!)}
          />
        </Can>
      }
    >
      <ReportResults<DeviceLockUnlockEvent>
        pagination={pagination}
        columns={columns}
        rowKey="commandId"
        values={filters.values}
        onSortChange={filters.setFilters}
        onPageSizeChange={filters.setPageSize}
        blocked={!filters.query}
      />
    </PageLayout>
  )
}

function dash(v: string) {
  return v || '—'
}
